import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Search, Sparkles, TrendingUp, TrendingDown, Minus, Target,
  DollarSign, AlertTriangle, BarChart3, Zap, Brain, Shield,
  ArrowRight, ChevronDown, ChevronRight, Loader2, RefreshCw,
  Award, Percent, Scale, Lightbulb, LineChart
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StrategyFinder = ({ onStrategySelect }) => {
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [result, setResult] = useState(null);
  const [expandedStrategy, setExpandedStrategy] = useState(null);
  const [sortBy, setSortBy] = useState('overall');

  const analyzeStock = async () => {
    if (!symbol.trim()) {
      toast.error('Please enter a stock symbol');
      return;
    }

    setLoading(true);
    setProgress(0);
    setResult(null);

    try {
      // Simulate progress updates
      setProgress(10);
      setProgressText('Fetching market data...');
      
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          const increment = Math.random() * 15;
          const messages = [
            'Analyzing price trends with GPT-5.2...',
            'Generating strategies with Claude...',
            'Assessing risks with Gemini...',
            'Calculating probabilities...',
            'Ranking strategies...'
          ];
          setProgressText(messages[Math.floor(Math.random() * messages.length)]);
          return Math.min(prev + increment, 90);
        });
      }, 1500);

      const response = await axios.post(`${API}/strategy-finder/analyze`, {
        symbol: symbol.toUpperCase()
      }, { timeout: 120000 }); // 2 minute timeout for AI analysis

      clearInterval(progressInterval);
      setProgress(100);
      setProgressText('Analysis complete!');

      if (response.data.success) {
        setResult(response.data);
        toast.success(`Found ${response.data.total_strategies} strategies for ${symbol.toUpperCase()}`);
      } else {
        toast.error(response.data.error || 'Analysis failed');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      toast.error(err.response?.data?.detail || 'Failed to analyze stock');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch(trend?.toLowerCase()) {
      case 'bullish': return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'bearish': return <TrendingDown className="h-4 w-4 text-red-500" />;
      default: return <Minus className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStrategyTypeColor = (type) => {
    switch(type?.toLowerCase()) {
      case 'bullish': return 'bg-green-100 text-green-700 border-green-200';
      case 'bearish': return 'bg-red-100 text-red-700 border-red-200';
      case 'neutral': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'volatile': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getRiskColor = (riskScore) => {
    if (riskScore <= 3) return 'text-green-600';
    if (riskScore <= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const sortStrategies = (strategies) => {
    if (!strategies) return [];
    
    const sorted = [...strategies];
    switch(sortBy) {
      case 'probability':
        return sorted.sort((a, b) => (a.rank_by_profit_probability || 999) - (b.rank_by_profit_probability || 999));
      case 'risk_reward':
        return sorted.sort((a, b) => (a.rank_by_risk_reward || 999) - (b.rank_by_risk_reward || 999));
      case 'max_profit':
        return sorted.sort((a, b) => (a.rank_by_max_profit || 999) - (b.rank_by_max_profit || 999));
      default:
        return sorted.sort((a, b) => (b.overall_score || 0) - (a.overall_score || 0));
    }
  };

  const StrategyCard = ({ strategy, index }) => {
    const isExpanded = expandedStrategy === index;
    
    return (
      <div className="border rounded-lg mb-3 overflow-hidden hover:shadow-md transition-shadow">
        <div 
          className="p-4 cursor-pointer bg-white hover:bg-gray-50"
          onClick={() => setExpandedStrategy(isExpanded ? null : index)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 font-bold text-sm">
                #{index + 1}
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="font-semibold">{strategy.strategy_name}</span>
                  <Badge className={`text-xs ${getStrategyTypeColor(strategy.type || 'neutral')}`}>
                    {strategy.type || 'Neutral'}
                  </Badge>
                </div>
                <div className="text-sm text-gray-500">
                  {strategy.legs?.length || 0} leg{strategy.legs?.length !== 1 ? 's' : ''} • {strategy.rationale?.substring(0, 60)}...
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Scores */}
              <div className="text-center">
                <div className="text-xs text-gray-500">Prob. of Profit</div>
                <div className="font-bold text-green-600">{strategy.probability_of_profit || '?'}%</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500">Risk</div>
                <div className={`font-bold ${getRiskColor(strategy.risk_score || 5)}`}>
                  {strategy.risk_score || '?'}/10
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500">Score</div>
                <div className="font-bold text-indigo-600">{strategy.overall_score || '?'}</div>
              </div>
              
              {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </div>
          </div>
        </div>
        
        {/* Expanded Details */}
        {isExpanded && (
          <div className="border-t bg-gray-50 p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white p-3 rounded-lg">
                <div className="text-xs text-gray-500 flex items-center">
                  <DollarSign className="h-3 w-3 mr-1" />
                  Max Profit
                </div>
                <div className="font-semibold text-green-600">{strategy.max_profit || 'N/A'}</div>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <div className="text-xs text-gray-500 flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Max Loss
                </div>
                <div className="font-semibold text-red-600">{strategy.max_loss || 'N/A'}</div>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <div className="text-xs text-gray-500 flex items-center">
                  <Target className="h-3 w-3 mr-1" />
                  Break Even
                </div>
                <div className="font-semibold">{strategy.break_even || 'N/A'}</div>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <div className="text-xs text-gray-500 flex items-center">
                  <Scale className="h-3 w-3 mr-1" />
                  Risk/Reward
                </div>
                <div className="font-semibold">{strategy.risk_reward_ratio || 'N/A'}</div>
              </div>
            </div>
            
            {/* Legs */}
            {strategy.legs && strategy.legs.length > 0 && (
              <div className="mb-4">
                <div className="text-sm font-medium mb-2">Position Legs:</div>
                <div className="space-y-2">
                  {strategy.legs.map((leg, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white p-2 rounded text-sm">
                      <div className="flex items-center space-x-2">
                        <Badge variant={leg.action === 'buy' ? 'default' : 'secondary'}>
                          {leg.action?.toUpperCase()}
                        </Badge>
                        <span>{leg.quantity || 1}x</span>
                        <span className={leg.type === 'call' ? 'text-green-600' : 'text-red-600'}>
                          {leg.type?.toUpperCase()}
                        </span>
                        <span>@ ${leg.strike}</span>
                      </div>
                      <span className="text-gray-500">{leg.expiration}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Rationale */}
            <div className="mb-4">
              <div className="text-sm font-medium mb-1">Why this strategy:</div>
              <p className="text-sm text-gray-600">{strategy.rationale}</p>
            </div>
            
            {/* Warnings */}
            {strategy.warnings && strategy.warnings.length > 0 && (
              <div className="mb-4">
                <div className="text-sm font-medium mb-1 text-amber-600 flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Risk Warnings:
                </div>
                <ul className="text-sm text-gray-600 list-disc list-inside">
                  {strategy.warnings.map((w, idx) => (
                    <li key={idx}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Best For */}
            {strategy.best_for && (
              <div className="mb-4">
                <Badge variant="outline" className="bg-indigo-50">
                  <Lightbulb className="h-3 w-3 mr-1" />
                  Best for: {strategy.best_for}
                </Badge>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex space-x-2">
              <Button 
                size="sm" 
                onClick={(e) => {
                  e.stopPropagation();
                  if (onStrategySelect) {
                    onStrategySelect(strategy, 'plchart');
                  }
                }}
              >
                <LineChart className="h-4 w-4 mr-1" />
                View P/L Chart
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onStrategySelect) {
                    onStrategySelect(strategy, 'analyze');
                  }
                }}
              >
                <Brain className="h-4 w-4 mr-1" />
                Deep Analysis
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="glass-effect border-0 shadow-lg" data-testid="strategy-finder">
      <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
        <CardTitle className="flex items-center">
          <Sparkles className="h-6 w-6 mr-2" />
          AI Strategy Finder
        </CardTitle>
        <CardDescription className="text-indigo-100">
          Multi-AI analysis using GPT-5.2 + Claude + Gemini to find optimal option strategies
        </CardDescription>
      </CardHeader>

      <CardContent className="p-6">
        {/* Search Input */}
        <div className="flex space-x-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Enter stock symbol (e.g., AAPL, TSLA, NVDA)"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              onKeyPress={(e) => e.key === 'Enter' && analyzeStock()}
              className="pl-10 text-lg"
              data-testid="strategy-symbol-input"
            />
          </div>
          <Button 
            onClick={analyzeStock}
            disabled={loading || !symbol.trim()}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 px-6"
            data-testid="find-strategies-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Zap className="h-4 w-4 mr-2" />
                Find Strategies
              </>
            )}
          </Button>
        </div>

        {/* Progress Bar */}
        {loading && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">{progressText}</span>
              <span className="text-sm font-medium">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
            <div className="flex items-center justify-center space-x-4 mt-3 text-xs text-gray-500">
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-1 ${progress > 10 ? 'bg-green-500' : 'bg-gray-300'}`} />
                GPT-5.2
              </div>
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-1 ${progress > 40 ? 'bg-green-500' : 'bg-gray-300'}`} />
                Claude
              </div>
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-1 ${progress > 70 ? 'bg-green-500' : 'bg-gray-300'}`} />
                Gemini
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="space-y-6">
            {/* Market Summary */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl font-bold">{result.symbol}</span>
                  <span className="text-gray-500">{result.market_data?.company_name}</span>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">${result.market_data?.current_price?.toFixed(2)}</div>
                  <div className={`text-sm ${result.market_data?.change_30d_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {result.market_data?.change_30d_pct >= 0 ? '+' : ''}{result.market_data?.change_30d_pct?.toFixed(2)}% (30d)
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Trend:</span>
                  <div className="flex items-center font-medium">
                    {getTrendIcon(result.market_analysis?.trend)}
                    <span className="ml-1 capitalize">{result.market_analysis?.trend || 'Neutral'}</span>
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Volatility:</span>
                  <div className="font-medium">{result.market_data?.volatility_30d?.toFixed(1)}%</div>
                </div>
                <div>
                  <span className="text-gray-500">IV:</span>
                  <div className="font-medium">{result.market_data?.avg_iv?.toFixed(1)}%</div>
                </div>
                <div>
                  <span className="text-gray-500">Sentiment:</span>
                  <div className="font-medium">{result.market_analysis?.sentiment_score || '?'}/10</div>
                </div>
              </div>
            </div>

            {/* Sort Options */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Award className="h-5 w-5 text-indigo-600" />
                <span className="font-semibold">{result.total_strategies} Strategies Found</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Sort by:</span>
                <Tabs value={sortBy} onValueChange={setSortBy} className="w-auto">
                  <TabsList className="h-8">
                    <TabsTrigger value="overall" className="text-xs px-2 h-6">Overall</TabsTrigger>
                    <TabsTrigger value="probability" className="text-xs px-2 h-6">Probability</TabsTrigger>
                    <TabsTrigger value="risk_reward" className="text-xs px-2 h-6">Risk/Reward</TabsTrigger>
                    <TabsTrigger value="max_profit" className="text-xs px-2 h-6">Max Profit</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>

            {/* Strategy Cards */}
            <ScrollArea className="h-[500px]">
              {sortStrategies(result.strategies).map((strategy, index) => (
                <StrategyCard key={index} strategy={strategy} index={index} />
              ))}
            </ScrollArea>
          </div>
        )}

        {/* Empty State */}
        {!loading && !result && (
          <div className="text-center py-12 text-gray-500">
            <Sparkles className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg mb-2">Enter a stock symbol to discover optimal strategies</p>
            <p className="text-sm">Our multi-AI system will analyze the stock and recommend the best option strategies</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StrategyFinder;
